/**
 * Chat conversation persistence service.
 * Calls the main backend (port 8000) at /api/v1/chat/...
 * Every function returns null on any error so callers can fall back to localStorage.
 */
import axios from "axios";
import type { Conversation, ConversationSummary, StoredMessage } from "../types/multiChatbot";

const BASE =
  (process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1").replace(/\/$/, "");

function authHeaders(): Record<string, string> {
  const token =
    typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listConversations(): Promise<ConversationSummary[] | null> {
  try {
    const res = await axios.get<ConversationSummary[]>(`${BASE}/chat/conversations`, {
      headers: authHeaders(),
    });
    return res.data;
  } catch {
    return null;
  }
}

export async function createConversation(
  title = "New conversation"
): Promise<Conversation | null> {
  try {
    const res = await axios.post<Conversation>(
      `${BASE}/chat/conversations`,
      { title },
      { headers: authHeaders() }
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function getConversation(id: string): Promise<Conversation | null> {
  try {
    const res = await axios.get<Conversation>(`${BASE}/chat/conversations/${id}`, {
      headers: authHeaders(),
    });
    return res.data;
  } catch {
    return null;
  }
}

export async function deleteConversation(id: string): Promise<boolean> {
  try {
    await axios.delete(`${BASE}/chat/conversations/${id}`, { headers: authHeaders() });
    return true;
  } catch {
    return false;
  }
}

export async function renameConversation(
  id: string,
  title: string
): Promise<Conversation | null> {
  try {
    const res = await axios.patch<Conversation>(
      `${BASE}/chat/conversations/${id}`,
      { title },
      { headers: authHeaders() }
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function appendMessages(
  conversationId: string,
  messages: StoredMessage[]
): Promise<boolean> {
  try {
    await axios.post(
      `${BASE}/chat/conversations/${conversationId}/messages`,
      { messages },
      { headers: authHeaders() }
    );
    return true;
  } catch {
    return false;
  }
}
